import { useEffect, useState } from "react";
import API from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { MagnifyingGlass } from "@phosphor-icons/react";
import SubJobRow from "./SubJobRow";

/* Turn an existing standalone job into a sub-job of `parent`. */
export default function AttachJobDialog({ open, onOpenChange, parent, onAttached }) {
  const [q, setQ] = useState("");
  const [sameClient, setSameClient] = useState(true);
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      API.get(`/jobs/${parent.id}/sub-jobs/candidates`, { params: { q, same_client: sameClient } })
        .then(({ data }) => setRows(data.jobs || [])).catch(() => setRows([]));
    }, 200);
    return () => clearTimeout(t);
  }, [open, q, sameClient, parent?.id]);

  const attach = async (job) => {
    setBusy(job.id);
    try {
      const { data } = await API.post(`/jobs/${parent.id}/sub-jobs/attach`, { job_id: job.id });
      toast.success(data.message);
      onAttached?.(job);
      setRows((r) => (r || []).filter((x) => x.id !== job.id));
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not attach job"); }
    finally { setBusy(""); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="attach-job-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Chivo]">Attach an existing job to {parent?.reference}</DialogTitle>
          <DialogDescription className="text-xs">Already logged the issues as separate jobs? Pull them under this main job so they're tracked together. The job keeps its reference, assignee and schedule.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-2.5 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by reference, title or address…" className="pl-8 h-8 text-xs" autoFocus data-testid="attach-job-search" />
          </div>
          <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground whitespace-nowrap cursor-pointer">
            <input type="checkbox" className="rounded" checked={sameClient} onChange={(e) => setSameClient(e.target.checked)} data-testid="attach-job-same-client" /> Same client only
          </label>
        </div>
        <div className="border border-border rounded-md divide-y divide-border max-h-80 overflow-y-auto" data-testid="attach-job-results">
          {rows === null ? <p className="p-4 text-xs text-muted-foreground">Searching…</p>
            : rows.length === 0 ? <p className="p-4 text-xs text-muted-foreground">No standalone jobs match{sameClient && parent?.client_name ? ` for ${parent.client_name}` : ""}.</p>
            : rows.map((j) => (
              <SubJobRow key={j.id} job={{ ...j, sub_job_index: "" }} compact testid={`attach-candidate-${j.id}`}
                actions={<Button size="sm" variant="outline" className="h-7 text-[11px] mr-2" disabled={busy === j.id} onClick={() => attach(j)} data-testid={`attach-job-${j.id}`}>{busy === j.id ? "Attaching…" : "Attach"}</Button>} />
            ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
